import React from "react";

// Apollo Client
import { gql, useMutation } from "@apollo/client";

// Material-UI Components
import Dialog from "@material-ui/core/Dialog";
import Slide from "@material-ui/core/Slide";
import MuiDialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";

// Material-UI Styles
import { withStyles } from "@material-ui/core/styles";

// External Libraries
import deepEqual from "fast-deep-equal";

// Icons
import closeIcon from "../../../../assets/icons/close.svg"; // Close icon

// External Utilities
import { uploadClient } from "../../../..";
import { convertToString } from "../../../../helper";

// Custom Components
import Footer from "./components/Footer";
import MailingListSection from "./components/MailingListSection";
import RequestForm from "./components/RequestForm";
import TemplateSection from "./components/TemplateSection";

// Utilities
import { notify } from "../../../Notify/Notify";

// Stylesheets
import "./RequestMutationPopup.scss";

// GraphQL Queries
import { GET_ALL_REQUESTS } from "../../RightSection/RightSection";
import { GET_REQUEST_DETAILS_BY_ID } from "../../../RequestDetailedView/RequestDetailedView";

// Custom Utilities
import { validateRequestMutationState } from "./utils/RequestMutationPopupUtilsFuns";
import { hasUniqueEmails } from "../../Drawers/MailingListDrawer";

// MUI Related
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// Graphql Related
const CREATE_DOCUMENT_REQUEST = gql`
  mutation CreateDocumentRequest(
    $documentRequestTemplateId: String
    $dueDate: String!
    $emailDescription: String
    $files: [Upload]
    $filesInfo: String
    $mailingListId: String
    $mailingListUsers: [DocumentRequestMailingListUserInputType]
    $recurringInterval: String
    $reminders: String!
    $removedDocuments: [String]
    $removedMailingListUsers: [String]
    $title: String!
    $updatedDocumentFiles: [Upload]
    $updatedDocumentFilesInfo: [UpdatedRequestDocumentsInputType]
    $documentsWithoutFile: [String]
  ) {
    createDocumentRequest(
      documentRequestTemplateId: $documentRequestTemplateId
      dueDate: $dueDate
      emailDescription: $emailDescription
      files: $files
      filesInfo: $filesInfo
      mailingListId: $mailingListId
      mailingListUsers: $mailingListUsers
      recurringInterval: $recurringInterval
      reminders: $reminders
      removedDocuments: $removedDocuments
      removedMailingListUsers: $removedMailingListUsers
      title: $title
      updatedDocumentFiles: $updatedDocumentFiles
      updatedDocumentFilesInfo: $updatedDocumentFilesInfo
      documentsWithoutFile: $documentsWithoutFile
    ) {
      success
    }
  }
`;

export const UPDATE_DOCUMENT_REQUEST = gql`
  mutation UpdateDocumentRequest(
    $id: String!
    $dueDate: String
    $emailDescription: String
    $title: String
    $recurringInterval: String
    $reminders: String
    $removedDocuments: [String]
    $removedMailingListUsers: [String]
    $files: [Upload]
    $filesInfo: String
    $updatedDocumentFiles: [Upload]
    $updatedDocumentFilesInfo: [UpdatedRequestDocumentsInputType]
    $mailingListUsers: [DocumentRequestMailingListUserInputType]
    $documentsWithoutFile: [String]
  ) {
    updateDocumentRequest(
      id: $id
      dueDate: $dueDate
      emailDescription: $emailDescription
      title: $title
      recurringInterval: $recurringInterval
      reminders: $reminders
      files: $files
      filesInfo: $filesInfo
      removedDocuments: $removedDocuments
      removedMailingListUsers: $removedMailingListUsers
      updatedDocumentFiles: $updatedDocumentFiles
      updatedDocumentFilesInfo: $updatedDocumentFilesInfo
      mailingListUsers: $mailingListUsers
      documentsWithoutFile: $documentsWithoutFile
    ) {
      success
    }
  }
`;

const styles = (theme) => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 0,
    padding: "20px",
    height: "72px",
    borderBottom: "1px solid #e2e8f0",
  },
  closeButton: {},
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      {children}

      {onClose ? (
        <div>
          <img
            className="close"
            src={closeIcon}
            alt="closeIcon"
            onClick={onClose}
          />
        </div>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    paddingTop: "0px",
    paddingBottom: "0px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: 0,
    borderTop: "1px solid #e2e8f0",
  },
}))(MuiDialogActions);

function RequestMutationPopup({
  isOpen = false,
  isEdit = false,
  state = {},
  onChange = () =>
    console.log("OnChange is not provided to RequestMutationPopup"),
  onClose = () =>
    console.log("onClose is not provided in RequestMutationPopup"),
}) {
  // Create Related
  const [createRequest, { loading: isCreateRequestMutationLoading }] =
    useMutation(CREATE_DOCUMENT_REQUEST, {
      client: uploadClient,
      refetchQueries: [{ query: GET_ALL_REQUESTS }],
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Create Request Mutation Error:", error);
      },
      onCompleted: () => {
        const title = "Success";
        const detail = `Request ${convertToString(
          state.title
        )} is successfully created.`;
        const type = "success";
        notify(title, detail, type);
        onClose();
      },
      awaitRefetchQueries: true,
    });

  const createdRequestHandler = () => {
    const payload = transformStateToMutationPayload(state);
    if (!payload) return;
    createRequest({ variables: { ...payload } });
  };

  // Update Related

  const [updateDocumentRequest, { loading: isUpdateDocumentRequestLoading }] =
    useMutation(UPDATE_DOCUMENT_REQUEST, {
      client: uploadClient,
      refetchQueries: [
        {
          query: GET_REQUEST_DETAILS_BY_ID,
          variables: { id: state?.id ?? "no-id-is-coming-from-update-request" },
        },
      ],
      onCompleted: (data) => {
        const title = "Success";
        const detail = `Request ${convertToString(
          state.title
        )} is successfully updated.`;
        const type = "success";
        notify(title, detail, type);
        onClose();
      },
      onError: (error) => {
        // Handle errors
        console.error("Error updating request:", error);
        // Additional error handling if needed
      },
      awaitRefetchQueries: true,
    });
  const updateRequestHandler = () => {
    const payload = createUpdatePayload(state?.initialStateForEdit, state);
    updateDocumentRequest({
      variables: { ...payload },
    });
  };

  const isMutationButtonDisabled =
    !deepEqual(validateRequestMutationState(state), {}) ||
    isCreateRequestMutationLoading ||
    isUpdateDocumentRequestLoading;
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      className="request-mutation-modal"
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle id="customized-dialog-title" onClose={onClose}>
        <h4 className="modal-title">
          {`${isEdit ? "Edit" : "Create"}  Request`}
        </h4>
      </DialogTitle>

      <DialogContent className="new-design-v1 request-mutation-modal-body">
        {" "}
        <RequestForm onChange={onChange} state={state} />
        <TemplateSection onChange={onChange} state={state} isEdit={isEdit} />
        <MailingListSection onChange={onChange} state={state} isEdit={isEdit} />
      </DialogContent>
      <DialogActions>
        <div className="new-design-v1 request-mutation-modal-footer">
          <Footer
            isEdit={isEdit}
            isMutationButtonDisabled={isMutationButtonDisabled}
            onSubmitHandler={
              isEdit ? updateRequestHandler : createdRequestHandler
            }
            onClose={onClose}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default RequestMutationPopup;

/**
 * Transforms the provided state into a mutation payload for creating or updating a document request.
 *
 * @param {object} state - The state object containing information for the document request.
 * @param {string} state.title - The title of the document request.
 * @param {string} state.dueDate - The due date for the document request.
 * @param {string} state.reminders - Reminders for the document request.
 * @param {string} state.recurringRequest - The recurring interval for the document request.
 * @param {string} state.emailDescription - The email description for the document request.
 * @param {object} state.currentSelectTemplate - The selected document request template.
 * @param {Array} state.requiredDocs - An array of required documents for the document request.
 * @param {object} state.currentSelectMailingList - The selected mailing list for the document request.
 * @param {Array} state.users - An array of users associated with the document request.
 * @param {Array} state.requiredDocsFromTemplate - Required documents obtained from the template.
 * @param {Array} state.usersFromMailingList - Users obtained from the selected mailing list.
 *
 * @returns {object} A mutation payload for creating or updating a document request.
 */
const transformStateToMutationPayload = (state) => {
  const {
    title,
    dueDate,
    reminders,
    recurringRequest,
    emailDescription,
    currentSelectTemplate,
    requiredDocs,
    currentSelectMailingList,
    users,
    requiredDocsFromTemplate = [],
    usersFromMailingList = [],
  } = state;

  const payload = {};

  const addedUsers = users.filter(
    (user) => !usersFromMailingList.some((u) => u.email === user.email)
  );

  if (addedUsers.length > 0) {
    if (!hasUniqueEmails(addedUsers)) {
      notify(
        "Error",
        "Duplicate emails found in the recipients list.",
        "error"
      );
      return false;
    }
    payload["mailingListUsers"] = addedUsers?.map(({ email, name }) => ({
      email,
      name,
    }));
  }
  const removedUsers = usersFromMailingList.filter(
    (user) => !users.some((u) => u.email === user.email)
  );
  if (removedUsers.length > 0) {
    payload["removedMailingListUsers"] = removedUsers.map(
      (removedUser) => removedUser.id
    );
  }
  const docsPayload = getDocumentChanges(
    requiredDocsFromTemplate,
    requiredDocs
  );
  if (currentSelectTemplate.id !== "") {
    payload["documentRequestTemplateId"] = currentSelectTemplate.id;
  }
  if (currentSelectMailingList.id !== "") {
    payload["mailingListId"] = currentSelectMailingList.id;
  }
  return {
    title,
    dueDate,
    recurringInterval: recurringRequest,
    reminders,
    emailDescription,
    ...payload,
    ...docsPayload,
  };
};

/**
 * Creates a payload for updating a request based on the changes between initial and final states.
 *
 * @param {object} initialState - The initial state of the request.
 * @param {string} initialState.id - The ID of the request.
 * @param {string} initialState.title - The title of the request.
 * @param {string} initialState.dueDate - The due date of the request.
 * @param {string} initialState.emailDescription - The email description of the request.
 * @param {string} initialState.reminders - The reminders of the request.
 * @param {string} initialState.recurringRequest - The recurring interval of the request.
 * @param {Array} initialState.users - An array of users associated with the request.
 * @param {Array} initialState.requiredDocs - An array of required documents for the request.
 *
 * @param {object} finalState - The final state of the request.
 * @param {string} finalState.id - The ID of the request.
 * @param {string} finalState.title - The title of the request.
 * @param {string} finalState.dueDate - The due date of the request.
 * @param {string} finalState.emailDescription - The email description of the request.
 * @param {string} finalState.reminders - The reminders of the request.
 * @param {string} finalState.recurringRequest - The recurring interval of the request.
 * @param {Array} finalState.users - An array of users associated with the request.
 * @param {Array} finalState.requiredDocs - An array of required documents for the request.
 *
 * @returns {object} A payload representing the changes between the initial and final states of the request.
 */
function createUpdatePayload(initialState = {}, finalState = {}) {
  const payload = {
    id: finalState.id,
  };

  if (finalState.title !== initialState.title) {
    payload.title = finalState.title;
  }

  if (finalState.dueDate !== initialState.dueDate) {
    payload.dueDate = finalState.dueDate;
  }

  if (finalState.emailDescription !== initialState.emailDescription) {
    payload.emailDescription = finalState.emailDescription;
  }

  if (finalState.reminders !== initialState.reminders) {
    payload.reminders = finalState.reminders;
  }

  if (finalState.recurringRequest !== initialState.recurringRequest) {
    payload.recurringInterval = finalState.recurringRequest;
  }
  // Handle User list
  const { users: finalUsers = [] } = finalState;
  const { users: initialUsers = [] } = initialState;
  const addedUsers = finalUsers
    .filter((user) => !initialUsers.some((u) => u.email === user.email))
    .map(({ email = "", name = "" }) => ({ email, name }));
  if (addedUsers.length > 0) {
    payload["mailingListUsers"] = addedUsers;
  }
  const removedUsers = initialUsers.filter(
    (user) => !finalUsers.some((u) => u.email === user.email)
  );
  if (removedUsers.length > 0) {
    payload["removedMailingListUsers"] = removedUsers.map(
      (removedUser) => removedUser.id
    );
  }
  const { requiredDocs: finalRequiredDocs } = finalState;
  const { requiredDocs: initialRequiredDocs } = initialState;
  const docsPayload = getDocumentChanges(
    initialRequiredDocs,
    finalRequiredDocs
  );
  // Handle docs
  return { ...payload, ...docsPayload };
}

/**
 * Compares initial and final states of required documents and generates a payload
 * representing the changes, including added, edited, and deleted documents.
 *
 * @param {Array} initialRequiredDocuments - An array of initial required documents.
 * @param {Array} finalRequiredDocuments - An array of final required documents.
 *
 * @returns {object} A payload representing the changes in required documents.
 *                   The payload includes information about added, edited, and deleted documents.
 */

function getDocumentChanges(initialRequiredDocuments, finalRequiredDocuments) {
  const payload = {};
  const changes = {
    added: [],
    editedNames: [],
    deleted: [],
    editedDocs: [],
  };

  // Check for added and edited documents
  finalRequiredDocuments.forEach((finalDoc) => {
    const matchingDoc = initialRequiredDocuments.find(
      (initDoc) => initDoc.documentId === finalDoc.documentId
    );

    if (!matchingDoc) {
      changes.added.push(finalDoc);
    } else {
      const isNameChanged = matchingDoc.documentName !== finalDoc.documentName;
      const isFileNameChanged =
        matchingDoc.documentFileName !== finalDoc.documentFileName;
      if (isNameChanged && isFileNameChanged) {
        changes.added.push(finalDoc);
        changes.deleted.push(finalDoc);
        return;
      }
      if (
        !(isNameChanged && isFileNameChanged) &&
        (isNameChanged || isFileNameChanged)
      ) {
        const editedDoc = {
          documentId: finalDoc.documentId,
          documentFile:
            finalDoc.documentFile || matchingDoc.documentFile || null,
        };

        if (isNameChanged) {
          editedDoc["documentName"] = finalDoc.documentName;
          changes.editedNames.push(editedDoc);
          return;
        }

        if (isFileNameChanged) {
          editedDoc["documentFileName"] = finalDoc.documentFileName;
          changes.editedDocs.push(editedDoc);
        }
      }
    }
  });

  // Check for deleted documents
  initialRequiredDocuments.forEach((initDoc) => {
    const matchingDoc = finalRequiredDocuments.find(
      (finalDoc) => initDoc.documentId === finalDoc.documentId
    );

    if (!matchingDoc) {
      changes.deleted.push(initDoc);
    }
  });

  if (changes.added.length > 0) {
    payload["files"] = changes.added
      .filter((u) => u.documentFile !== null)
      .map((doc) => doc.documentFile);
    const filesInfo = changes.added
      .filter((u) => u.documentFile !== null)
      .reduce((acc, doc) => {
        acc[doc.documentFileName] = doc.documentName;
        return acc;
      }, {});
    payload["filesInfo"] = JSON.stringify(filesInfo);
    const documentsWithoutFile = changes.added
      .filter((u) => u.documentFile === null)
      .map((doc) => doc.documentName);
    payload["documentsWithoutFile"] = documentsWithoutFile;
  }
  if (changes.deleted.length > 0) {
    payload["removedDocuments"] = changes.deleted.map(
      (element) => element.documentId
    );
  }
  if (changes.editedDocs.length > 0) {
    payload["updatedDocumentFiles"] = changes.editedDocs.map(
      (doc) => doc.documentFile
    );
    payload["updatedDocumentFilesInfo"] = changes.editedDocs.map((doc) => ({
      documentId: doc.documentId,
      newFileUpload: doc.documentFileName,
    }));
  }

  if (changes.editedNames.length > 0) {
    if ("updatedDocumentFilesInfo" in payload) {
      payload["updatedDocumentFilesInfo"] = [
        ...payload["updatedDocumentFilesInfo"],
        ...changes.editedNames.map((doc) => ({
          documentId: doc.documentId,
          newDocumentName: doc.documentName,
        })),
      ];
    } else {
      payload["updatedDocumentFilesInfo"] = changes.editedNames.map((doc) => ({
        documentId: doc.documentId,
        newDocumentName: doc.documentName,
      }));
    }
  }
  return payload;
}
